import { useSnackbar } from "@portal/components/Snackbar"
import { Button, Divider, Icon, MenuItem, menuItemClasses, Stack, styled, TextField, Typography } from "@tm/components"
import { useCreateCustomArticleList, useCreateCustomRepairTimeList } from "@tm/data/basket"
import { useLocalization } from "@tm/localization"
import { useCallback, useState, KeyboardEvent, ChangeEvent, useRef, useLayoutEffect } from "react"

type Props = {
    variant: "partList" | "workList"
    onSaveError: (error: string) => void
    hasCustomItems: boolean
}

const TransparentMenuItem = styled(MenuItem)({
    ":hover": {
        backgroundColor: "transparent",
    },
    [`&.${menuItemClasses.focusVisible}`]: {
        backgroundColor: "transparent",
    },
})

export function NewCustomListMenuItem({ variant, onSaveError, hasCustomItems }: Props) {
    const [isCreationMode, setIsCreationMode] = useState<boolean>()
    const [newListName, setNewListName] = useState<string>("")
    const textfieldRef = useRef<HTMLInputElement>(null)

    const { translateText } = useLocalization()
    const { mutateAsync: createPartList, isLoading: isCreatingWorkList } = useCreateCustomArticleList()
    const { mutateAsync: createWorkList, isLoading: isCreatingPartList } = useCreateCustomRepairTimeList()
    const { addMessage: toast } = useSnackbar()

    const isCreating = isCreatingPartList || isCreatingWorkList
    const isPartList = variant === "partList"

    const enableCreationMode = () => {
        setIsCreationMode(true)
    }

    const disableCreationMode = () => {
        setIsCreationMode(false)
    }

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setNewListName(e.currentTarget.value)
    }

    const createList = useCallback(async () => {
        // Don't trigger a request if new list name is empty, show a toast message instead
        if (!newListName) {
            toast(translateText(14133), "error", undefined, false)
            return
        }

        onSaveError("")
        try {
            if (isPartList) {
                await createPartList({ customListToCreate: { name: newListName } })
            } else {
                await createWorkList({ customListToCreate: { name: newListName } })
            }

            disableCreationMode()
        } catch {
            onSaveError(translateText(13452))
        }
    }, [createPartList, createWorkList, isPartList, newListName, onSaveError, toast, translateText])

    const handleKeyDown = async (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") {
            await createList()
        }
    }

    useLayoutEffect(() => {
        const textfield = textfieldRef.current

        if (textfield) {
            textfield.scrollIntoView()
        }
    })

    return (
        <>
            {hasCustomItems && <Divider />}
            {isCreationMode ? (
                <TransparentMenuItem disableRipple onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5} width="100%" mt={0.75}>
                        <TextField
                            ref={textfieldRef}
                            label={translateText(14134)}
                            value={newListName}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            disabled={isCreating}
                            sx={{ marginTop: 0, width: "100%" }}
                            autoFocus
                            fullWidth
                        />
                        <Button disabled={isCreating} onClick={disableCreationMode} startIcon={<Icon name="cancel-x" />} />
                        <Button disabled={isCreating} onClick={createList} startIcon={<Icon name="check" />} color="success" />
                    </Stack>
                </TransparentMenuItem>
            ) : (
                <MenuItem onClick={enableCreationMode}>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <Icon name="plus" />
                        <Typography>{translateText(13948)}</Typography>
                    </Stack>
                </MenuItem>
            )}
        </>
    )
}
